import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import { api } from "../../services/api/api.service";
import { API_COMPETITIONS, API_COMPETITIONS_INIT } from "../../services/api/api.urls";
import {toastr} from "react-redux-toastr";
import { DartsCompetition, DartsCompetitionsState } from "./CompetitionsCRUD.types";
import { IdName } from "../../common/types/IdName";
import { COMPANY_ABBR } from "../../common/constants/company";
import { TryCatch } from "../../services/TryCatch";

export interface IGetCompetitionsResponse {
    totalItems: 0,
    totalPages: 0,
    pageNum: 0,
    pageSize: 0,
    items: DartsCompetition[]
}

export interface ICompetitionInitResponse {
    clients: IdName[],
    types: IdName[],

}

const initialState: DartsCompetitionsState = {
    dartsCompetitions: [] as DartsCompetition[],
    competition: {
        name: '',
        clientId: 0,
        clientName: "",
        id: 0,
        isActive: false,
        type: 0,
        typeName: ""
    },
    isLoading: true,
    isVisibleForm: false,
    editMode: false,
    editCompetitionId: null,
    noContent: null,
    errorMessage: '',
    pagination: {
        totalPages: 0,
        activePage: 1,
        pageSize: 10,
    },
    clients: [],
    competitionTypeOptions: []

}

export const getDartsCompetitions = createAsyncThunk<IGetCompetitionsResponse, string, { rejectValue: string }>(
    'dartsCompetitions/get',
    async (query, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.get(`${API_COMPETITIONS}?${query}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to fetch darts competitions.");
            return rejectWithValue(result.error);
        }
        toastr.success(COMPANY_ABBR, "User registered");
        return result.data;
    }
)

export const getCompetitionInitOptions = createAsyncThunk<ICompetitionInitResponse, void, { rejectValue: string }>(
    'competition/get-all-clients',
    async (_, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.get(`${API_COMPETITIONS_INIT}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to fetch all client.");
            return rejectWithValue(result.error);
        }
        toastr.success(COMPANY_ABBR, "User registered");
        return result.data;
    }
)

export const getDartsSingleCompetition = createAsyncThunk<DartsCompetition, number, { rejectValue: string }>(
    'dartsCompetitions/getSingle',
    async (id, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.get(API_COMPETITIONS + `/${id}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to fetch darts competition.");
            return rejectWithValue(result.error);
        }
        return result.data;
    }
)


export const createDartsCompetition = createAsyncThunk<DartsCompetition, DartsCompetition, { rejectValue: string }>(
    'dartsCompetitions/post',
    async (payload, {rejectWithValue}) => {
        let status = 0;
        const result = await TryCatch(
            () => api.post(API_COMPETITIONS, payload).then(res => { status = res.status; return res.data; })
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, "Failed to create a competition.");
            return rejectWithValue(result.error);
        }
        if (status === 200 || status === 204) {
            toastr.warning(COMPANY_ABBR, "This competition is already exist.");
            //    return rejectWithValue("This game is already exist");
        }
        if (status === 201) {
            toastr.success(COMPANY_ABBR, "Competition has been created.");
        }
        return result.data;
    }
)


export const editDartsCompetition = createAsyncThunk<DartsCompetition, DartsCompetition, { rejectValue: string }>(
    'dartsCompetitions/edit',
    async (payload, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.put(`${API_COMPETITIONS}`, payload).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, result.error);
            return rejectWithValue(result.error);
        }
        toastr.success(COMPANY_ABBR, "Darts competition has been edited");
        return result.data;
    }
)

export const deleteDartsCompetition = createAsyncThunk<DartsCompetition, number, { rejectValue: string }>(
    'dartsCompetitions/delete',
    async (id, {rejectWithValue}) => {
        const result = await TryCatch(
            () => api.delete(`${API_COMPETITIONS}/${id}`).then(res => res.data)
        );
        if (!result.success) {
            toastr.error(COMPANY_ABBR, result.error);
            return rejectWithValue(result.error);
        }
        toastr.success(COMPANY_ABBR, "Darts competition has been deleted.");
        return result.data;
    }
);

export const CompetitionsCRUD = createSlice({name: 'CompetitionsCRUD',
    reducers: {
        changePageSize: (state, action) => {
            state.pagination.pageSize = action.payload
        },
        showCompetitionForm: (state) => {
            state.isVisibleForm = true;
        },
        hideCompetitionForm: (state,) => {
            state.isVisibleForm = false;
            state.editMode = false;
        },

        setPageNumber: (state, action) => {
            state.pagination.activePage = action.payload
        },
        setEditModeCompetitionForm: (state, action: PayloadAction<number | null>) => {
            state.editMode = true;
            state.isVisibleForm = true;
            state.editCompetitionId = action.payload;
        },
        clearSingleCompetition: (state) => {
            state.competition = {
                name: '',
                clientId: 0,
                isActive: false,
                type: 0,
                clientName: "",
                id: 0,
                typeName: ""
            };
        }
    },
    initialState,
    extraReducers: (builder) => {
        builder
            .addCase(getDartsCompetitions.fulfilled, (state, action) => {
                state.dartsCompetitions = action.payload.items;
                state.noContent = !action.payload.items.length;
                state.isLoading = false;
                state.pagination.totalPages = action.payload.totalPages
            })
            .addCase(getCompetitionInitOptions.fulfilled, (state, action) => {
                state.clients = action.payload.clients
                state.competitionTypeOptions = action.payload.types
            })
            .addCase(getDartsCompetitions.rejected, (state) => {
                state.isLoading = false;

            })
            .addCase(getDartsSingleCompetition.fulfilled, (state, action) => {
                state.competition = action.payload;
            })
            .addCase(deleteDartsCompetition.pending, (state) => {
                state.isLoading = true;

            })
            .addCase(deleteDartsCompetition.fulfilled, (state, action) => {
                if (action.payload !== undefined) {
                    state.dartsCompetitions = state.dartsCompetitions.filter(
                        (competition) => competition.id !== action.meta.arg
                    );
                }
                state.isLoading = false;

            })
            .addCase(deleteDartsCompetition.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(createDartsCompetition.pending, (state) => {
                state.isVisibleForm = false;
            })
            .addCase(createDartsCompetition.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(createDartsCompetition.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(editDartsCompetition.pending, (state) => {
                state.isVisibleForm = false;
                state.editMode = false;
            })
            .addCase(editDartsCompetition.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(editDartsCompetition.rejected, (state) => {
                state.isLoading = false;
            });
    },
});

export const {
    showCompetitionForm,
    hideCompetitionForm,
    setPageNumber,
    setEditModeCompetitionForm,
    clearSingleCompetition,
    changePageSize
} = CompetitionsCRUD.actions
export default CompetitionsCRUD.reducer;

